import React, { useRef, useEffect } from "react";
import ApexCharts, { ApexOptions } from "apexcharts";
import { STEP_CONFIGS } from "../../app/pages/dashboards/steps/stepConfig";
import { Collaborator } from "../../types/team";

export interface StepAssignmentInfo {
  assignedToEmail?: string | null;
  assignedRole?: string | null;
}

interface Props {
  stepCompletionMap: Record<string, number>;
  collaborators: Collaborator[];
  stepAssignments?: Record<string, StepAssignmentInfo>;
}

const STRATEGIST_COLOR = "#1D4ED8";
const EXECUTOR_COLOR  = "#065F46";
const STRATEGIST_BG   = "#DBEAFE";
const EXECUTOR_BG     = "#D1FAE5";

export const TeamProgressChart: React.FC<Props> = ({
  stepCompletionMap,
  collaborators,
  stepAssignments = {},
}) => {
  const chartRef     = useRef<HTMLDivElement>(null);
  const chartInst    = useRef<ApexCharts | undefined>(undefined);
  const prevKey      = useRef<string>("");

  // ── Derive role-based stats from STEP_CONFIGS defaultRole ──────────────────
  const strategistStepNums = STEP_CONFIGS
    .filter((s) => s.defaultRole === "strategist")
    .map((s) => s.stepNumber);
  const executorStepNums = STEP_CONFIGS
    .filter((s) => s.defaultRole === "executor")
    .map((s) => s.stepNumber);

  const strategistCompleted = strategistStepNums.filter(
    (n) => (stepCompletionMap[`step${n}`] ?? 0) > 0
  ).length;
  const executorCompleted = executorStepNums.filter(
    (n) => (stepCompletionMap[`step${n}`] ?? 0) > 0
  ).length;

  const strategistPct = strategistStepNums.length > 0
    ? Math.round((strategistCompleted / strategistStepNums.length) * 100)
    : 0;
  const executorPct = executorStepNums.length > 0
    ? Math.round((executorCompleted / executorStepNums.length) * 100)
    : 0;

  // ── Find team members by role ───────────────────────────────────────────────
  const strategistMembers = collaborators.filter((c) => c.role === "strategist");
  const executorMembers   = collaborators.filter((c) => c.role === "executor");

  const memberName = (email: string) => email.split("@")[0];

  const strategistMemberStr = strategistMembers.length > 0
    ? strategistMembers.map((m) => memberName(m.email)).join(", ")
    : null;
  const executorMemberStr = executorMembers.length > 0
    ? executorMembers.map((m) => memberName(m.email)).join(", ")
    : null;

  const sCategory = [
    "Strategy",
    strategistMemberStr ? `· ${strategistMemberStr}` : "",
    `· ${strategistCompleted}/${strategistStepNums.length} steps`,
  ].filter(Boolean).join(" ");

  const eCategory = [
    "Execution",
    executorMemberStr ? `· ${executorMemberStr}` : "",
    `· ${executorCompleted}/${executorStepNums.length} steps`,
  ].filter(Boolean).join(" ");

  // ── Render / update chart ──────────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return;

    const key = `${strategistPct}:${executorPct}:${sCategory}:${eCategory}`;
    if (key === prevKey.current && chartInst.current) return;
    prevKey.current = key;

    const options: ApexOptions = {
      chart: {
        type:        "bar",
        height:      150,
        toolbar:     { show: false },
        animations:  { enabled: true, speed: 600, easing: "easeinout" },
        fontFamily:  "inherit",
        background:  "transparent",
        sparkline:   { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal:   true,
          barHeight:    "52%",
          borderRadius: 4,
          distributed:  true,
          dataLabels:   { position: "center" },
        },
      },
      colors: [STRATEGIST_COLOR, EXECUTOR_COLOR],
      dataLabels: {
        enabled:   true,
        formatter: (val: number) => `${val}%`,
        style: {
          fontSize:  "12px",
          fontWeight: "700",
          colors:    ["#FFFFFF"],
        },
      },
      series: [
        {
          name: "Progress",
          data: [strategistPct, executorPct],
        },
      ],
      xaxis: {
        categories: [sCategory, eCategory],
        max:        100,
        min:        0,
        labels: {
          formatter: (val: string) => `${val}%`,
          style:     { fontSize: "10px", colors: ["#94A3B8"] },
        },
        axisBorder: { show: false },
        axisTicks:  { show: false },
      },
      yaxis: {
        labels: {
          style:    { fontSize: "11px", colors: ["#475569"] },
          maxWidth: 220,
        },
      },
      grid: {
        borderColor: "#F1F5F9",
        xaxis:       { lines: { show: true } },
        yaxis:       { lines: { show: false } },
        padding:     { left: 0, right: 16, top: -8, bottom: -8 },
      },
      legend: { show: false },
      tooltip: {
        enabled: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        custom: ({ dataPointIndex }: any) => {
          const isStrategist = dataPointIndex === 0;
          const completed    = isStrategist ? strategistCompleted : executorCompleted;
          const total        = isStrategist ? strategistStepNums.length : executorStepNums.length;
          const pct          = isStrategist ? strategistPct : executorPct;
          const color        = isStrategist ? STRATEGIST_COLOR : EXECUTOR_COLOR;
          const bg           = isStrategist ? STRATEGIST_BG : EXECUTOR_BG;
          const label        = isStrategist ? "Strategy" : "Execution";
          return `<div style="padding:8px 12px;font-size:12px;color:${color};background:${bg};border-radius:6px;font-weight:600;white-space:nowrap;">${label}: ${pct}% — ${completed}/${total} steps complete</div>`;
        },
      },
      states: {
        hover:  { filter: { type: "lighten", value: 0.1 } },
        active: { filter: { type: "none" } },
      },
    };

    if (chartInst.current) {
      chartInst.current.destroy();
      chartInst.current = undefined;
    }

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();
    chartInst.current = chart;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategistPct, executorPct, sCategory, eCategory]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (chartInst.current) {
        chartInst.current.destroy();
        chartInst.current = undefined;
      }
    };
  }, []);

  // ── Step-level detail: which steps are assigned to whom ───────────────────
  // Find any step where an assignment overrides the defaultRole
  const assignedStrategistUIDs = new Set<string>();
  const assignedExecutorUIDs   = new Set<string>();
  Object.values(stepAssignments).forEach((a) => {
    if (a.assignedRole === "strategist" && a.assignedToEmail) assignedStrategistUIDs.add(a.assignedToEmail);
    if (a.assignedRole === "executor"   && a.assignedToEmail) assignedExecutorUIDs.add(a.assignedToEmail);
  });

  return (
    <div
      style={{
        background:    "#FFFFFF",
        border:        "1px solid #E2E8F0",
        borderRadius:  "12px",
        padding:       "16px 20px",
        marginBottom:  "16px",
        boxShadow:     "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize:      "13px",
          fontWeight:    600,
          color:         "#475569",
          marginBottom:  "4px",
          display:       "flex",
          alignItems:    "center",
          gap:           "6px",
        }}
      >
        <i className="bi bi-people-fill" style={{ fontSize: "14px" }}></i>
        Team Progress by Role
        <span
          style={{
            marginLeft:  "auto",
            fontSize:    "11px",
            fontWeight:  400,
            color:       "#94A3B8",
            whiteSpace:  "nowrap",
          }}
        >
          {strategistStepNums.length} strategy · {executorStepNums.length} execution
        </span>
      </div>

      {/* ApexCharts container */}
      <div ref={chartRef} style={{ marginLeft: "-8px" }} />

      {/* Member legend (only if collaborators have assigned roles) */}
      {(strategistMembers.length > 0 || executorMembers.length > 0) && (
        <div
          style={{
            display:       "flex",
            gap:           "16px",
            flexWrap:      "wrap",
            marginTop:     "4px",
            paddingTop:    "10px",
            borderTop:     "1px solid #F1F5F9",
          }}
        >
          {strategistMembers.length > 0 && (
            <span
              style={{
                display:    "flex",
                alignItems: "center",
                gap:        "5px",
                fontSize:   "11px",
                color:      "#475569",
              }}
            >
              <span
                style={{
                  width:        "8px",
                  height:       "8px",
                  borderRadius: "9999px",
                  background:   STRATEGIST_COLOR,
                  flexShrink:   0,
                }}
              />
              Strategy: {strategistMembers.map((m) => m.email).join(", ")}
            </span>
          )}
          {executorMembers.length > 0 && (
            <span
              style={{
                display:    "flex",
                alignItems: "center",
                gap:        "5px",
                fontSize:   "11px",
                color:      "#475569",
              }}
            >
              <span
                style={{
                  width:        "8px",
                  height:       "8px",
                  borderRadius: "9999px",
                  background:   EXECUTOR_COLOR,
                  flexShrink:   0,
                }}
              />
              Execution: {executorMembers.map((m) => m.email).join(", ")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
